import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs/Subscription';
import { FormBuilder, FormGroup, FormControl, Validators } from '@angular/forms';

import { ProductsService } from '../products.service';
import { NotificationService } from 'app/core/services/notification.service';
import { ConfigService } from 'app/core/services/config.service';
import { UtilService } from 'app/core/services/util.service';

@Component({
  selector: 'itpx-product-edit',
  templateUrl: './product-edit.component.html',
  styleUrls: ['./product-edit.component.css']
})
export class ProductEditComponent implements OnInit {

    private id: string;
    private details: any;
    private saveUrl: string;
    private cancelRoute: string = '/agora/product-data/products';

    private form: FormGroup;

    constructor(
        private service: ProductsService,
        private configService: ConfigService,
        private notificationService: NotificationService,
        private router: Router,
        private route: ActivatedRoute,
        private fb: FormBuilder,
        private utilService: UtilService
    ) { }

    ngOnInit() {
        this.buildForm();
        this.route.params.subscribe(params => {
            this.id = params['id'];
            this.service.getProductDetails(this.id)
                .subscribe(
                    body => {
                        this.setDetails(<any>body['data']);
                        this.patchForm(this.details);
                    }
                );
        });
    }

    buildForm() {
        this.form = this.fb.group({
            name: [ null, Validators.required ],
            hosted: [null],
            sku: [ { value: null, disabled: true } ],
            keywords: [ [] ],
            domains: [ [] ],
            categoryFamily: [ [] ],
            shortDescription: [ null, Validators.required ],
            quotation: [ null ],
            online: [ null ],
            available: [ null ],
            enriched: [ null ],
            editableCourseware: [ null ],
            'type.id': [ null, Validators.required ],
            'level.id': [ null ],
            'segment.id': [ null ],
            'group.id': [ null, Validators.required ],
            brand: [ null ],
            credits: this.fb.group({
                royaltyPoints: [ null ],
                credits: [ null ],
                pmiCredits: [ null ],
                apdCredits: [ null ]
            }),
            details: this.fb.group({
                accreditor: [ null ],
                releaseNote: [ null ],
                anticipatedNextRelease: [ null ],
                sampleFileUri: [ null ],
                leadAuthor: [ null ],
                coAuthor: [ null ],
                qualityBrand: [ null ],
                longDescription: [ null ],
                features: [ null ],
                sellerStatement: [ null ],
                version: [ null ]
            }),
            instructions: this.fb.group({
                examLocations: [ null ],
                prerequisites: [ null ],
                openBookExamRequirements: [ null ],
                instructorRequirements: [ null ],
                proctorRequirements: [ null ],
                technicalRequirements: [ null ]
            }),
            pricing: this.fb.group({
                listPrice: [ null, Validators.required ],
                'discountCategory.id': [ null, Validators.required ]
            }),
            render: this.fb.group({
                largeImageUri: [ null ],
                mediumImageUri: [ null ],
                smallImageUri: [ null ],
                newImageKey: [ null ],
                newImageBucket: [ null ]
            }),
            salesInfo: this.fb.group({
                baseUnit: [ null ],
                packingUnit: [ null ],
                puBuRatio: [ null ],
                eanCode: [ null ],
                minimumOrderQuantity: [ null ],
                maximumOrderQuantity: [ null ],
                stepQuantity: [ null ]
            }),
            courseInfo: this.fb.group({
                agenda: [ null ],
                description: [ null ],
                outline: [ null ],
                learningObjectives: [ null ],
                targetAudience: [ null ],
                prerequisites: [ null ],
                duration: [ null ],
                'durationType.id': [ null ]
            }),
            examInfo: this.fb.group({
                extraTime: [ null ],
                fulfillment: [ null ],
                planning: [ null ],
                questions: [ null ],
                passGrade: [ null ],
                passGradeInstructors: [ null ],
                questionFormat: [ null ],

            })
        })
    }

    patchForm(details: any) {
        this.form.patchValue({
            name: details.name,
            hosted: details.hosted,
            sku: details.sku,
            keywords: details.keywords,
            domains: details.domains,
            categoryFamily: details.categoryFamily,
            shortDescription: details.shortDescription,
            quotation: details.quotation,
            online: details.online,
            available: details.available,
            enriched: details.enriched,
            editableCourseware: details.editableCourseware,
            'type.id': details.type ? details.type.id : null,
            'level.id': details.level ? details.level.id : null,
            'segment.id': details.segment ? details.segment.id : null,
            'group.id': details.group ? details.group.id : null,
            credits: {
                royaltyPoints: details.credits.royaltyPoints,
                credits: details.credits.credits,
                pmiCredits: details.credits.pmiCredits,
                apdCredits: details.credits.apdCredits
            },
            details: {
                accreditor: details.details.accreditor,
                releaseNote: details.details.releaseNote,
                anticipatedNextRelease: details.details.anticipatedNextRelease,
                sampleFileUri: details.details.sampleFileUri,
                leadAuthor: details.details.leadAuthor,
                coAuthor: details.details.coAuthor,
                qualityBrand: details.details.qualityBrand,
                longDescription: details.details.longDescription,
                features: details.details.features,
                sellerStatement: details.details.sellerStatement,
                version: details.details.version
            },
            instructions: {
                examLocations: details.instructions.examLocations,
                prerequisites: details.instructions.prerequisites,
                openBookExamRequirements: details.instructions.openBookExamRequirements,
                instructorRequirements: details.instructions.instructorRequirements,
                proctorRequirements: details.instructions.proctorRequirements,
                technicalRequirements: details.instructions.technicalRequirements
            },
            pricing: {
                listPrice: details.pricing.listPrice,
                'discountCategory.id': details.pricing.discountCategory ? details.pricing.discountCategory.id : null
            },
            render: {
                largeImageUri: details.render.largeImageUri,
                mediumImageUri: details.render.mediumImageUri,
                smallImageUri: details.render.smallImageUri,
                newImageKey: details.render.newImageKey,
                newImageBucket: details.render.newImageBucket,
            },
            salesInfo: {
                baseUnit: details.salesInfo.baseUnit,
                packingUnit: details.salesInfo.packingUnit,
                puBuRatio: details.salesInfo.puBuRatio,
                eanCode: details.salesInfo.eanCode,
                minimumOrderQuantity: details.salesInfo.minimumOrderQuantity,
                maximumOrderQuantity: details.salesInfo.maximumOrderQuantity,
                stepQuantity: details.salesInfo.stepQuantity
            },
            courseInfo: {
                agenda: details.courseInfo.agenda,
                description: details.courseInfo.description,
                outline: details.courseInfo.outline,
                learningObjectives: details.courseInfo.learningObjectives,
                targetAudience: details.courseInfo.targetAudience,
                prerequisites: details.courseInfo.prerequisites,
                duration: details.courseInfo.duration,
                'durationType.id': details.courseInfo.durationType ? details.courseInfo.durationType.id : null,
            },
            examInfo: {
                extraTime: details.examInfo.extraTime,
                fulfillment: details.examInfo.fulfillment,
                planning: details.examInfo.planning,
                questions: details.examInfo.questions,
                passGrade: details.examInfo.passGrade,
                passGradeInstructors: details.examInfo.passGradeInstructors,
                questionFormat: details.examInfo.questionFormat,
            }
        });
    }

    setDetails(details: any) {
        details.domains = details.domains ? details.domains.map(_ => { return _.id }) : [];
        details.categoryFamily = details.categoryFamily ? details.categoryFamily.map(_ => { return _.id }) : [];
        this.details = details;
    }

    private update() {
        this.service.edit(this.service.formatData(this.details))
            .subscribe(
                body => {
                    this.notificationService.success('Successfully updated the product', 'Success');
                    this.setDetails(<any>body['data']);
                }
            );
    }

    private onSubmit() {
        let form = this.form.value;

        let unflatten = this.utilService.unflatten(form);
        unflatten['pricing'] = this.utilService.unflatten(form.pricing);
        unflatten['courseInfo'] = this.utilService.unflatten(form.courseInfo);

        let request = this.service.formatData(unflatten);
        if(!request.segment || request.segment.id == 'null') {
            request.segment = null;
        }
        request.id = this.id;
        this.service.edit(request)
            .subscribe(
                data => {
                    this.notificationService.success('Successfully updated the product', 'Success');
                    this.router.navigate(['/agora/product-data/products/:id/details', {id: this.id}]);
                },
                error => {}
            )
    }

}



// WEBPACK FOOTER //
// ./src/app/agora/components/product-data/products/edit/product-edit.component.ts